/**
 * PropertiesPanel Component
 *
 * Displays and allows editing of the selected beat node's properties:
 * - Title
 * - Script (with markdown editor)
 * - Duration (in seconds)
 * - Image (browse button)
 *
 * The panel is empty when no node is selected.
 *
 * CONTROLLED INPUTS:
 * ==================
 * React uses "controlled components" - the input's value is controlled
 * by React state rather than the DOM. When the user types, we:
 * 1. Capture the new value in onChange
 * 2. Update our store with the new value
 * 3. React re-renders with the new value
 *
 * This ensures our store is always the source of truth.
 */

import { useCallback, memo, useEffect, CSSProperties, ChangeEvent, useState, useRef } from 'react';
import { useProjectStore, useSelectedNode } from '@/stores/projectStore';
import { useFocusedNodeId } from '@/stores/playbackStore';
import { useViewStore } from '@/stores/viewStore';
import { secondsToFrameNotation, frameNotationToSeconds } from '@/utils/frameUtils';
import { nodeColorPalette } from '@/styles/themes';
import type { CommentBoxNode, CameraMovementType, TextFontFamily, TextAlignment, TitleNode, OverlayPresetPosition } from '@/types';
import { formatMovementName, overlaySymbols } from '@/components/OutputWindow/overlayLibrary';

/**
 * PropertiesPanel Component
 */
function PropertiesPanel() {
  // ---------------------------------------------------------------------------
  // STORE ACCESS
  // ---------------------------------------------------------------------------

  const activeView = useViewStore((state) => state.activeView);
  const selectedNode = useSelectedNode();
  const nodes = useProjectStore((state) => state.nodes);
  const commentBoxes = useProjectStore((state) => state.commentBoxes);
  const referenceNodes = useProjectStore((state) => state.referenceNodes);
  const noteNodes = useProjectStore((state) => state.noteNodes);
  const selectedNodeIds = useProjectStore((state) => state.selectedNodeIds);
  const selectedReferenceIds = useProjectStore((state) => state.selectedReferenceIds);
  const selectedNoteIds = useProjectStore((state) => state.selectedNoteIds);
  const updateNode = useProjectStore((state) => state.updateNode);
  const updateCommentBox = useProjectStore((state) => state.updateCommentBox);
  const updateReferenceNode = useProjectStore((state) => state.updateReferenceNode);
  const updateNoteNode = useProjectStore((state) => state.updateNoteNode);
  const updateSelectedNodesColor = useProjectStore((state) => state.updateSelectedNodesColor);
  const moveSelectedNodesToShelf = useProjectStore((state) => state.moveSelectedNodesToShelf);
  const updateNodePosition = useProjectStore((state) => state.updateNodePosition);
  const projectPath = useProjectStore((state) => state.projectPath);
  const fps = useProjectStore((state) => state.fps);
  const projectName = useProjectStore((state) => state.projectName);
  const projectAuthor = useProjectStore((state) => state.projectAuthor);
  const projectDescription = useProjectStore((state) => state.projectDescription);
  const setProjectName = useProjectStore((state) => state.setProjectName);
  const setProjectAuthor = useProjectStore((state) => state.setProjectAuthor);
  const setProjectDescription = useProjectStore((state) => state.setProjectDescription);
  const focusedNodeId = useFocusedNodeId();
  const saveHistory = useProjectStore((state) => state.saveHistory);
  const addOverlay = useProjectStore((state) => state.addOverlay);
  const removeOverlay = useProjectStore((state) => state.removeOverlay);
  const updateOverlay = useProjectStore((state) => state.updateOverlay);
  const addSceneHeadingToHistory = useProjectStore((state) => state.addSceneHeadingToHistory);
  const selectedTitleIds = useProjectStore((state) => state.selectedTitleIds);
  const titleNodes = useProjectStore((state) => state.titleNodes);
  const updateTitleNode = useProjectStore((state) => state.updateTitleNode);
  const updateTitleNodeDuration = useProjectStore((state) => state.updateTitleNodeDuration);
  const updateTitleNodePosition = useProjectStore((state) => state.updateTitleNodePosition);
  const deleteTitleNode = useProjectStore((state) => state.deleteTitleNode);

  // State for collapsible camera overlay section
  const [isOverlaySectionExpanded, setIsOverlaySectionExpanded] = useState(false);
  const [expandedOverlayId, setExpandedOverlayId] = useState<string | null>(null);

  // Local state for debounced text inputs
  const [localTitle, setLocalTitle] = useState(''); // Beat node title
  const [localSceneHeading, setLocalSceneHeading] = useState('');
  const [localScript, setLocalScript] = useState('');
  const [localReferenceTitle, setLocalReferenceTitle] = useState(''); // Reference node title (separate!)
  const [localNotes, setLocalNotes] = useState('');
  const [localTags, setLocalTags] = useState('');
  const [localNoteTitle, setLocalNoteTitle] = useState(''); // Note node title
  const [localNoteContent, setLocalNoteContent] = useState(''); // Note node content
  const [localProjectName, setLocalProjectName] = useState(''); // Project name (syncs with top bar)
  const [localProjectAuthor, setLocalProjectAuthor] = useState(''); // Project author
  const [localProjectDescription, setLocalProjectDescription] = useState(''); // Project description
  const [localTitleText, setLocalTitleText] = useState(''); // Title node text (local for blur-only history save)

  // Scene heading autocomplete state
  const [showSceneHeadingDropdown, setShowSceneHeadingDropdown] = useState(false);
  const [sceneHeadingCursorIndex, setSceneHeadingCursorIndex] = useState(0);
  const [showAllSceneHeadings, setShowAllSceneHeadings] = useState(false); // Flag for double-click to show all
  const sceneHeadingHistory = useProjectStore((state) => state.sceneHeadingHistory);

  // Refs
  const scriptTextareaRef = useRef<HTMLTextAreaElement>(null);
  const sceneHeadingInputRef = useRef<HTMLInputElement>(null);
  const sceneHeadingDropdownRef = useRef<HTMLDivElement>(null);

  // Track last synced values to prevent debounced updates from overwriting external changes
  const lastSyncedScriptRef = useRef<string>('');
  const lastSyncedTitleRef = useRef<string>('');
  const lastSyncedSceneHeadingRef = useRef<string>('');

  // Check if selected item is a comment box
  const firstSelectedId = selectedNodeIds[0];
  const selectedCommentBox = firstSelectedId
    ? commentBoxes.find((box) => box.id === firstSelectedId)
    : null;

  // Check if selected item is a reference node
  const firstSelectedReferenceId = selectedReferenceIds[0];
  const selectedReference = firstSelectedReferenceId
    ? referenceNodes.find((ref) => ref.id === firstSelectedReferenceId)
    : null;

  // Check if selected item is a note node
  const firstSelectedNoteId = selectedNoteIds[0];
  const selectedNote = firstSelectedNoteId
    ? noteNodes.find((note) => note.id === firstSelectedNoteId)
    : null;

  // Check if selected item is a title node
  const firstSelectedTitleId = selectedTitleIds[0];
  const selectedTitle = firstSelectedTitleId
    ? titleNodes.find((title) => title.id === firstSelectedTitleId)
    : null;

  // Listen for rapid beat creation event to focus script input
  useEffect(() => {
    const handleFocusScriptInput = (event: Event) => {
      const customEvent = event as CustomEvent<{ nodeId: string }>;

      // Retry focus multiple times to ensure it works across browsers (especially Firefox)
      const attemptFocus = (attempts = 0) => {
        if (attempts >= 5) return; // Max 5 attempts

        setTimeout(() => {
          if (selectedNode?.id === customEvent.detail.nodeId && scriptTextareaRef.current) {
            scriptTextareaRef.current.focus();
            // Verify focus was successful
            if (document.activeElement !== scriptTextareaRef.current) {
              attemptFocus(attempts + 1);
            }
          } else if (attempts < 5) {
            attemptFocus(attempts + 1);
          }
        }, 50 * (attempts + 1)); // Exponential backoff: 50ms, 100ms, 150ms, 200ms, 250ms
      };

      attemptFocus();
    };

    window.addEventListener('focusScriptInput', handleFocusScriptInput);
    return () => window.removeEventListener('focusScriptInput', handleFocusScriptInput);
  }, [selectedNode?.id]);

  // Sync local state when selected node changes
  useEffect(() => {
    if (selectedNode) {
      const newTitle = selectedNode.data.title || '';
      const newSceneHeading = selectedNode.data.sceneHeading || '';
      const newScript = selectedNode.data.script || '';

      setLocalTitle(newTitle);
      setLocalSceneHeading(newSceneHeading);
      setLocalScript(newScript);

      // Update refs to mark these values as synced from store
      lastSyncedTitleRef.current = newTitle;
      lastSyncedSceneHeadingRef.current = newSceneHeading;
      lastSyncedScriptRef.current = newScript;
    }
    // Close scene heading dropdown when node changes or is deselected
    setShowSceneHeadingDropdown(false);
    setShowAllSceneHeadings(false);
  }, [selectedNode?.id]); // Only re-sync when node ID changes, not on every render

  // Sync local state when node data changes (e.g., when edited from ScriptWindow)
  useEffect(() => {
    if (selectedNode) {
      const newTitle = selectedNode.data.title || '';
      const newSceneHeading = selectedNode.data.sceneHeading || '';
      const newScript = selectedNode.data.script || '';

      setLocalTitle(newTitle);
      setLocalSceneHeading(newSceneHeading);
      setLocalScript(newScript);

      // Update refs to mark these values as synced from store
      lastSyncedTitleRef.current = newTitle;
      lastSyncedSceneHeadingRef.current = newSceneHeading;
      lastSyncedScriptRef.current = newScript;
    }
  }, [selectedNode?.data.title, selectedNode?.data.sceneHeading, selectedNode?.data.script]);

  // Sync local state when selected reference changes
  useEffect(() => {
    if (selectedReference) {
      setLocalReferenceTitle(selectedReference.data.title || ''); // Use separate state
      setLocalNotes(selectedReference.data.notes || '');
      setLocalTags(selectedReference.data.tags?.join(', ') || '');
    }
  }, [selectedReference?.id]);

  // Sync local state when selected note changes
  useEffect(() => {
    if (selectedNote) {
      setLocalNoteTitle(selectedNote.data.title || '');
      setLocalNoteContent(selectedNote.data.content || '');
    }
  }, [selectedNote?.id]);

  // Sync local state for project metadata
  useEffect(() => {
    const newName = projectName || '';
    const newAuthor = projectAuthor || '';
    const newDescription = projectDescription || '';

    setLocalProjectName(newName);
    setLocalProjectAuthor(newAuthor);
    setLocalProjectDescription(newDescription);
  }, [projectName, projectAuthor, projectDescription]);

  // Debounced update for title
  useEffect(() => {
    if (!selectedNode) return;

    const timeout = setTimeout(() => {
      // Only update if local value changed from last synced value (prevents overwriting external changes)
      if (localTitle !== lastSyncedTitleRef.current) {
        updateNode(selectedNode.id, { title: localTitle });
      }
    }, 300);

    return () => clearTimeout(timeout);
  }, [localTitle, selectedNode, updateNode]);

  // Debounced update for scene heading
  useEffect(() => {
    if (!selectedNode) return;

    const timeout = setTimeout(() => {
      // Only update if local value changed from last synced value
      if (localSceneHeading !== lastSyncedSceneHeadingRef.current) {
        updateNode(selectedNode.id, { sceneHeading: localSceneHeading });
      }
    }, 300);

    return () => clearTimeout(timeout);
  }, [localSceneHeading, selectedNode, updateNode]);

  // Debounced update for script
  useEffect(() => {
    if (!selectedNode) return;

    const timeout = setTimeout(() => {
      // Only update if local value changed from last synced value
      if (localScript !== lastSyncedScriptRef.current) {
        updateNode(selectedNode.id, { script: localScript });
      }
    }, 300);

    return () => clearTimeout(timeout);
  }, [localScript, selectedNode, updateNode]);

  // Debounced update for reference title
  useEffect(() => {
    if (!selectedReference) return;

    const timeout = setTimeout(() => {
      if (localReferenceTitle !== (selectedReference.data.title || '')) {
        updateReferenceNode(selectedReference.id, { title: localReferenceTitle });
      }
    }, 300);

    return () => clearTimeout(timeout);
  }, [localReferenceTitle, selectedReference, updateReferenceNode]);

  // Debounced update for reference notes
  useEffect(() => {
    if (!selectedReference) return;

    const timeout = setTimeout(() => {
      if (localNotes !== (selectedReference.data.notes || '')) {
        updateReferenceNode(selectedReference.id, { notes: localNotes });
      }
    }, 300);

    return () => clearTimeout(timeout);
  }, [localNotes, selectedReference, updateReferenceNode]);

  // Debounced update for reference tags
  useEffect(() => {
    if (!selectedReference) return;

    const timeout = setTimeout(() => {
      const currentTags = selectedReference.data.tags?.join(', ') || '';
      if (localTags !== currentTags) {
        // Parse comma-separated tags
        const tagsArray = localTags
          .split(',')
          .map((tag) => tag.trim())
          .filter((tag) => tag.length > 0);
        updateReferenceNode(selectedReference.id, { tags: tagsArray });
      }
    }, 300);

    return () => clearTimeout(timeout);
  }, [localTags, selectedReference, updateReferenceNode]);

  // Debounced update for note title
  useEffect(() => {
    if (!selectedNote) return;

    const timeout = setTimeout(() => {
      if (localNoteTitle !== (selectedNote.data.title || '')) {
        updateNoteNode(selectedNote.id, { title: localNoteTitle });
      }
    }, 300);

    return () => clearTimeout(timeout);
  }, [localNoteTitle, selectedNote, updateNoteNode]);

  // Debounced update for note content
  useEffect(() => {
    if (!selectedNote) return;

    const timeout = setTimeout(() => {
      if (localNoteContent !== (selectedNote.data.content || '')) {
        updateNoteNode(selectedNote.id, { content: localNoteContent });
      }
    }, 300);

    return () => clearTimeout(timeout);
  }, [localNoteContent, selectedNote, updateNoteNode]);

  // Debounced update for project name
  useEffect(() => {
    const timeout = setTimeout(() => {
      if (localProjectName !== (projectName || '')) {
        setProjectName(localProjectName);
      }
    }, 300);

    return () => clearTimeout(timeout);
  }, [localProjectName, projectName, setProjectName]);

  // Debounced update for project author
  useEffect(() => {
    const timeout = setTimeout(() => {
      if (localProjectAuthor !== (projectAuthor || '')) {
        setProjectAuthor(localProjectAuthor);
      }
    }, 300);

    return () => clearTimeout(timeout);
  }, [localProjectAuthor, projectAuthor, setProjectAuthor]);

  // Debounced update for project description
  useEffect(() => {
    const timeout = setTimeout(() => {
      if (localProjectDescription !== (projectDescription || '')) {
        setProjectDescription(localProjectDescription);
      }
    }, 300);

    return () => clearTimeout(timeout);
  }, [localProjectDescription, projectDescription, setProjectDescription]);

  // Sync local title text when selected title changes
  useEffect(() => {
    if (selectedTitle) {
      setLocalTitleText(selectedTitle.data.text);
    }
  }, [selectedTitle?.id]);

  // ---------------------------------------------------------------------------
  // EVENT HANDLERS
  // ---------------------------------------------------------------------------

  /**
   * Handle title change (updates local state, debounced update to store)
   */
  const handleTitleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setLocalTitle(event.target.value);
    },
    []
  );

  /**
   * Handle scene heading change (updates local state, debounced update to store)
   */
  const handleSceneHeadingChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value;
      setLocalSceneHeading(value);

      // Show dropdown with fuzzy matches as user types
      // Keep it open even if empty (will show presets)
      setShowSceneHeadingDropdown(true);
      setSceneHeadingCursorIndex(0);
      // User is typing, so show filtered results
      setShowAllSceneHeadings(false);
    },
    []
  );

  /**
   * Fuzzy search for scene headings
   * Includes: presets, history, AND all scene headings actually used in nodes
   */
  const getFuzzyMatches = useCallback((query: string): string[] => {
    // Presets that should always be available
    const presets = [
      'INT. LOCATION - DAY',
      'INT. LOCATION - NIGHT',
      'EXT. LOCATION - DAY',
      'EXT. LOCATION - NIGHT',
    ];

    // Get all scene headings actually used in nodes
    const usedInNodes = nodes
      .map(node => node.data.sceneHeading?.trim())
      .filter((heading): heading is string => !!heading && heading.length > 0);

    // Combine presets, used headings (prioritized), and history
    // Used headings come first so they appear at top of list
    const allOptions = [...new Set([...usedInNodes, ...presets, ...sceneHeadingHistory])];

    // If query is empty, return all options (for double-click)
    if (!query.trim()) {
      return allOptions.slice(0, 10);
    }

    const lowerQuery = query.toLowerCase();

    // Filter and sort by fuzzy match score
    const matches = allOptions
      .filter(heading => {
        const lowerHeading = heading.toLowerCase();
        // Simple fuzzy match: all query characters must appear in order
        let queryIndex = 0;
        for (let i = 0; i < lowerHeading.length && queryIndex < lowerQuery.length; i++) {
          if (lowerHeading[i] === lowerQuery[queryIndex]) {
            queryIndex++;
          }
        }
        return queryIndex === lowerQuery.length;
      })
      .sort((a, b) => {
        // Prioritize exact prefix matches
        const aLower = a.toLowerCase();
        const bLower = b.toLowerCase();
        const aStartsWith = aLower.startsWith(lowerQuery);
        const bStartsWith = bLower.startsWith(lowerQuery);

        if (aStartsWith && !bStartsWith) return -1;
        if (!aStartsWith && bStartsWith) return 1;

        // Then sort by position of first match
        const aIndex = aLower.indexOf(lowerQuery[0]);
        const bIndex = bLower.indexOf(lowerQuery[0]);
        return aIndex - bIndex;
      });

    // Remove duplicates while preserving order
    return Array.from(new Set(matches)).slice(0, 10);
  }, [sceneHeadingHistory, nodes]);

  /**
   * Handle scene heading keyboard navigation
   */
  const handleSceneHeadingKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (!showSceneHeadingDropdown) return;

      // Use the same logic as the dropdown rendering
      const matches = showAllSceneHeadings ? getFuzzyMatches('') : getFuzzyMatches(localSceneHeading);
      if (matches.length === 0) return;

      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault();
          setSceneHeadingCursorIndex((prev) => (prev + 1) % matches.length);
          break;
        case 'ArrowUp':
          event.preventDefault();
          setSceneHeadingCursorIndex((prev) => (prev - 1 + matches.length) % matches.length);
          break;
        case 'Enter':
          event.preventDefault();
          const selectedHeading = matches[sceneHeadingCursorIndex];
          setLocalSceneHeading(selectedHeading);
          setShowSceneHeadingDropdown(false);
          setShowAllSceneHeadings(false);
          // Add to history when user commits by selecting from dropdown
          addSceneHeadingToHistory(selectedHeading);
          break;
        case 'Escape':
          event.preventDefault();
          setShowSceneHeadingDropdown(false);
          setShowAllSceneHeadings(false);
          break;
      }
    },
    [showSceneHeadingDropdown, showAllSceneHeadings, localSceneHeading, sceneHeadingCursorIndex, getFuzzyMatches, addSceneHeadingToHistory]
  );

  /**
   * Handle clicking on a dropdown suggestion
   */
  const handleSuggestionClick = useCallback((heading: string) => {
    setLocalSceneHeading(heading);
    setShowSceneHeadingDropdown(false);
    setShowAllSceneHeadings(false);
    sceneHeadingInputRef.current?.focus();
    // Add to history when user commits by clicking suggestion
    addSceneHeadingToHistory(heading);
  }, [addSceneHeadingToHistory]);

  /**
   * Handle blur on scene heading input
   * Note: We don't add to history on blur - only when explicitly selecting from dropdown
   * This prevents typos from being saved as templates
   */
  const handleSceneHeadingBlur = useCallback(() => {
    // Just close the dropdown on blur
    setShowSceneHeadingDropdown(false);
    setShowAllSceneHeadings(false);
  }, []);

  /**
   * Handle double-click on scene heading input (show all options)
   */
  const handleSceneHeadingDoubleClick = useCallback(() => {
    setShowSceneHeadingDropdown(true);
    setSceneHeadingCursorIndex(0);
    // Show all presets regardless of current input
    setShowAllSceneHeadings(true);
  }, []);

  /**
   * Close dropdown when clicking outside
   */
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        sceneHeadingDropdownRef.current &&
        sceneHeadingInputRef.current &&
        !sceneHeadingDropdownRef.current.contains(event.target as Node) &&
        !sceneHeadingInputRef.current.contains(event.target as Node)
      ) {
        setShowSceneHeadingDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  /**
   * Handle script change (updates local state, debounced update to store)
   */
  const handleScriptChange = useCallback(
    (event: ChangeEvent<HTMLTextAreaElement>) => {
      setLocalScript(event.target.value);
    },
    []
  );

  /**
   * Handle duration change (frame notation)
   */
  const handleDurationChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      if (!selectedNode) return;
      const notation = event.target.value;
      // Convert frame notation to seconds
      const seconds = frameNotationToSeconds(notation, fps);
      if (seconds > 0) {
        updateNode(selectedNode.id, { duration: seconds });
      }
    },
    [selectedNode, updateNode, fps]
  );

  /**
   * Handle keyboard arrow keys for frame increment/decrement
   */
  const handleDurationKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (!selectedNode) return;

      if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
        event.preventDefault();

        const currentDuration = selectedNode.data.duration;
        const frameIncrement = 1 / fps; // Duration of one frame in seconds

        let newDuration: number;
        if (event.key === 'ArrowUp') {
          newDuration = currentDuration + frameIncrement;
        } else {
          newDuration = Math.max(frameIncrement, currentDuration - frameIncrement);
        }

        updateNode(selectedNode.id, { duration: newDuration });
      }
    },
    [selectedNode, updateNode, fps]
  );

  /**
   * Increment duration by one frame
   */
  const handleIncrementDuration = useCallback(() => {
    if (!selectedNode) return;
    saveHistory();
    const frameIncrement = 1 / fps;
    updateNode(selectedNode.id, { duration: selectedNode.data.duration + frameIncrement });
  }, [selectedNode, updateNode, fps, saveHistory]);

  /**
   * Decrement duration by one frame
   */
  const handleDecrementDuration = useCallback(() => {
    if (!selectedNode) return;
    saveHistory();
    const frameIncrement = 1 / fps;
    const newDuration = Math.max(frameIncrement, selectedNode.data.duration - frameIncrement);
    updateNode(selectedNode.id, { duration: newDuration });
  }, [selectedNode, updateNode, fps, saveHistory]);

  /**
   * Toggle node between shelf and main view
   */
  const handleToggleShelf = useCallback(() => {
    if (!selectedNode) return;

    // Handle multi-select: move all selected nodes to shelf
    if (selectedNodeIds.length > 1) {
      moveSelectedNodesToShelf();
      return;
    }

    saveHistory();
    const isMovingToShelf = !selectedNode.data.onShelf;

    if (isMovingToShelf) {
      // Store current position before moving to shelf
      updateNode(selectedNode.id, {
        onShelf: true,
        shelfStoredPosition: { x: selectedNode.position.x, y: selectedNode.position.y }
      });
    } else {
      const SPACING = 200; // Space between nodes
      const nodeIdToPlace = selectedNode.id;

      // Get all active (non-shelf) nodes excluding the one we're placing
      const activeNodes = nodes.filter((n) => !n.data.onShelf && n.id !== nodeIdToPlace);

      // Sort nodes by x position to get timeline order
      const sortedNodes = [...activeNodes].sort((a, b) => a.position.x - b.position.x);

      let targetX = 0;
      let targetY = 0;

      // If there's a focused node, we want to insert after it
      if (focusedNodeId) {
        const focusedIndex = sortedNodes.findIndex((n) => n.id === focusedNodeId);

        if (focusedIndex !== -1) {
          const focusedNode = sortedNodes[focusedIndex];
          targetX = focusedNode.position.x + SPACING;
          targetY = focusedNode.position.y;

          // Shift all nodes after the focused node to make room
          for (let i = focusedIndex + 1; i < sortedNodes.length; i++) {
            const currentNode = sortedNodes[i];
            const expectedMinX = targetX + (i - focusedIndex) * SPACING;

            // If this node is too close, shift it and all subsequent nodes
            if (currentNode.position.x < expectedMinX) {
              updateNodePosition(currentNode.id, {
                x: expectedMinX,
                y: currentNode.position.y,
              });
            }
          }
        } else {
          // Focused node not found in sorted list, place at end
          if (sortedNodes.length > 0) {
            const lastNode = sortedNodes[sortedNodes.length - 1];
            targetX = lastNode.position.x + SPACING;
            targetY = lastNode.position.y;
          }
        }
      } else {
        // No focused node, place at the end
        if (sortedNodes.length > 0) {
          const lastNode = sortedNodes[sortedNodes.length - 1];
          targetX = lastNode.position.x + SPACING;
          targetY = lastNode.position.y;
        }
      }

      // Move node from shelf and place it after current beat
      updateNode(selectedNode.id, { onShelf: false, shelfStoredPosition: undefined });
      updateNodePosition(selectedNode.id, { x: targetX, y: targetY });
    }
  }, [selectedNode, selectedNodeIds, nodes, focusedNodeId, updateNode, updateNodePosition, moveSelectedNodesToShelf, saveHistory]);

  /**
   * Handle browse button click for image selection
   */
  const handleBrowseImage = useCallback(async () => {
    if (!selectedNode || !window.electronAPI) return;

    // Show file selection dialog
    const result = await window.electronAPI.selectImage();
    if (!result.success || !result.filePath) return;

    // Get project directory (empty string for browser mode)
    const projectDir = projectPath ? projectPath.replace(/[/\\][^/\\]+$/, '') : '';

    // Copy image to project folder (or store blob URL in browser mode)
    const copyResult = await window.electronAPI.copyImage(
      result.filePath,
      projectDir
    );

    if (copyResult.success && copyResult.relativePath) {
      saveHistory();
      updateNode(selectedNode.id, { imagePath: copyResult.relativePath });
    }
  }, [selectedNode, updateNode, projectPath, saveHistory]);

  /**
   * Handle removing the image
   */
  const handleRemoveImage = useCallback(() => {
    if (!selectedNode) return;
    saveHistory();
    updateNode(selectedNode.id, { imagePath: null });
  }, [selectedNode, updateNode, saveHistory]);

  /**
   * Handle color change
   */
  const handleColorChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      if (!selectedNode) return;
      saveHistory();
      updateNode(selectedNode.id, { color: event.target.value });
    },
    [selectedNode, updateNode, saveHistory]
  );

  /**
   * Handle reset color to theme default
   */
  const handleResetColor = useCallback(() => {
    if (!selectedNode) return;
    saveHistory();
    updateNode(selectedNode.id, { color: undefined });
  }, [selectedNode, updateNode, saveHistory]);

  /**
   * Handle adding a camera overlay
   */
  const handleAddOverlay = useCallback((overlayType: CameraMovementType) => {
    if (!selectedNode) return;
    saveHistory();
    addOverlay(selectedNode.id, overlayType);
  }, [selectedNode, addOverlay, saveHistory]);

  /**
   * Handle removing a camera overlay
   */
  const handleRemoveOverlay = useCallback((overlayId: string) => {
    if (!selectedNode) return;
    saveHistory();
    removeOverlay(selectedNode.id, overlayId);
  }, [selectedNode, removeOverlay, saveHistory]);

  // ---------------------------------------------------------------------------
  // STYLES
  // ---------------------------------------------------------------------------

  const containerStyles: CSSProperties = {
    padding: 'var(--nb-space-sm)',
    overflow: 'auto',
    height: '100%',
  };

  const emptyStyles: CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    color: 'var(--nb-text-muted)',
  };

  const formStyles: CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    gap: 'var(--nb-space-sm)',
  };

  const fieldStyles: CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    gap: 'var(--nb-space-xs)',
  };

  const rowFieldStyles: CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--nb-space-sm)',
  };

  const labelStyles: CSSProperties = {
    fontSize: 'var(--nb-font-size-xs)',
    color: 'var(--nb-text-secondary)',
    fontWeight: 500,
  };

  const inputStyles: CSSProperties = {
    padding: 'var(--nb-space-xs) var(--nb-space-sm)',
    backgroundColor: 'var(--nb-bg-primary)',
    border: '1px solid var(--nb-border-primary)',
    borderRadius: 'var(--nb-radius-sm)',
    color: 'var(--nb-text-primary)',
    fontSize: 'var(--nb-font-size-sm)',
    outline: 'none',
  };

  const textareaStyles: CSSProperties = {
    ...inputStyles,
    minHeight: 200,
    resize: 'vertical',
    fontFamily: 'var(--nb-font-mono)',
    lineHeight: 1.5,
  };

  const numberInputStyles: CSSProperties = {
    ...inputStyles,
    width: 80,
  };

  const durationControlsStyles: CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
    marginLeft: 'var(--nb-space-xs)',
  };

  const spinnerButtonStyles: CSSProperties = {
    width: '18px',
    height: '12px',
    padding: 0,
    backgroundColor: 'var(--nb-bg-secondary)',
    border: '1px solid var(--nb-border-primary)',
    borderRadius: '2px',
    cursor: 'pointer',
    fontSize: '8px',
    lineHeight: '8px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'var(--nb-text-primary)',
  };

  const buttonStyles: CSSProperties = {
    padding: 'var(--nb-space-xs) var(--nb-space-sm)',
    backgroundColor: 'var(--nb-bg-hover)',
    border: '1px solid var(--nb-border-primary)',
    borderRadius: 'var(--nb-radius-sm)',
    color: 'var(--nb-text-primary)',
    cursor: 'pointer',
    fontSize: 'var(--nb-font-size-xs)',
  };

  const removeButtonStyles: CSSProperties = {
    ...buttonStyles,
    backgroundColor: 'transparent',
    color: 'var(--nb-accent-error)',
    borderColor: 'var(--nb-accent-error)',
  };

  const imageInfoStyles: CSSProperties = {
    fontSize: 'var(--nb-font-size-xs)',
    color: 'var(--nb-text-secondary)',
    marginLeft: 'var(--nb-space-sm)',
    flex: 1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  };

  // Camera overlay styles
  const overlayHeaderStyles: CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--nb-space-xs)',
    cursor: 'pointer',
    padding: 'var(--nb-space-xs)',
    borderRadius: 'var(--nb-radius-sm)',
    userSelect: 'none',
    transition: 'background-color var(--nb-transition-fast)',
  };

  const overlayContentStyles: CSSProperties = {
    marginTop: 'var(--nb-space-xs)',
    padding: 'var(--nb-space-sm)',
    backgroundColor: 'var(--nb-bg-secondary)',
    borderRadius: 'var(--nb-radius-sm)',
    border: '1px solid var(--nb-border-primary)',
  };

  const overlayDropdownStyles: CSSProperties = {
    width: '100%',
    padding: 'var(--nb-space-xs) var(--nb-space-sm)',
    backgroundColor: 'var(--nb-bg-primary)',
    border: '1px solid var(--nb-border-primary)',
    borderRadius: 'var(--nb-radius-sm)',
    color: 'var(--nb-text-primary)',
    fontSize: 'var(--nb-font-size-sm)',
    cursor: 'pointer',
  };

  const activeOverlayItemStyles: CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 'var(--nb-space-xs)',
    backgroundColor: 'var(--nb-bg-primary)',
    border: '1px solid var(--nb-border-primary)',
    borderRadius: 'var(--nb-radius-sm)',
    marginTop: 'var(--nb-space-xs)',
    fontSize: 'var(--nb-font-size-sm)',
  };

  const overlayIconStyles: CSSProperties = {
    marginRight: 'var(--nb-space-xs)',
  };

  // ---------------------------------------------------------------------------
  // RENDER
  // ---------------------------------------------------------------------------

  // Handle comment box properties
  if (selectedCommentBox && !selectedNode) {
    return (
      <div style={containerStyles} data-properties="true">
        <div style={formStyles}>
          {/* Comment Box Title */}
          <div style={fieldStyles}>
            <label style={labelStyles} title="The title of this comment box">
              Comment Box Title
            </label>
            <input
              type="text"
              value={selectedCommentBox.data.title}
              onChange={(e) => {
                updateCommentBox(selectedCommentBox.id, { 
                  data: { title: e.target.value } 
                });
              }}
              placeholder="Comment"
              style={inputStyles}
              title="Enter a title for this comment box"
            />
          </div>

          {/* Comment Box Color */}
          <div style={fieldStyles}>
            <label style={labelStyles} title="Color for this comment box">
              Color
            </label>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(5, 1fr)',
              gap: 'var(--nb-space-xs)',
              marginBottom: 'var(--nb-space-xs)',
            }}>
              {nodeColorPalette.map((color) => (
                <button
                  key={color.value}
                  onClick={() => {
                    saveHistory();
                    updateCommentBox(selectedCommentBox.id, { 
                      data: { color: color.value } 
                    });
                  }}
                  style={{
                    width: '100%',
                    height: 32,
                    backgroundColor: color.value,
                    border: selectedCommentBox.data.color === color.value
                      ? '3px solid var(--nb-text-primary)'
                      : '1px solid var(--nb-border-primary)',
                    borderRadius: 'var(--nb-radius-sm)',
                    cursor: 'pointer',
                    padding: 0,
                    transition: 'transform var(--nb-transition-fast), border var(--nb-transition-fast)',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'scale(1.1)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'scale(1)';
                  }}
                  title={color.name}
                />
              ))}
            </div>
            <div style={{ display: 'flex', gap: 'var(--nb-space-sm)', alignItems: 'center' }}>
              <input
                type="color"
                value={selectedCommentBox.data.color || '#E47019'}
                onChange={(e) => {
                  saveHistory();
                  updateCommentBox(selectedCommentBox.id, { 
                    data: { color: e.target.value } 
                  });
                }}
                style={{
                  ...inputStyles,
                  width: 60,
                  height: 32,
                  padding: 2,
                  cursor: 'pointer',
                }}
                title="Choose a custom color"
              />
              <span style={{ fontSize: 'var(--nb-font-size-xs)', color: 'var(--nb-text-muted)' }}>
                Custom
              </span>
            </div>
          </div>

          {/* Comment Box Notes */}
          <div style={fieldStyles}>
            <label style={labelStyles} title="Notes and thoughts about this comment box">
              Notes
            </label>
            <textarea
              value={selectedCommentBox.data.notes || ''}
              onChange={(e) => {
                updateCommentBox(selectedCommentBox.id, { 
                  data: { notes: e.target.value } 
                });
              }}
              placeholder="Add notes, thoughts, or reminders about this section..."
              style={textareaStyles}
              title="Write notes, thoughts, or reminders about the beats in this comment box"
            />
          </div>
        </div>
      </div>
    );
  }

  // Handle reference node properties (when in references view)
  if (activeView === 'references' && selectedReference) {
    return (
      <div style={containerStyles} data-properties="true">
        <div style={formStyles}>
          {/* Reference Title */}
          <div style={fieldStyles}>
            <label style={labelStyles} title="The name/title of this reference">
              Title
            </label>
            <input
              type="text"
              value={localReferenceTitle}
              onChange={(e) => setLocalReferenceTitle(e.target.value)}
              placeholder="New Reference"
              style={inputStyles}
              title="Enter a descriptive title for this reference"
            />
          </div>

          {/* Reference Notes */}
          <div style={fieldStyles}>
            <label style={labelStyles} title="Notes and description about this reference">
              Notes
            </label>
            <textarea
              value={localNotes}
              onChange={(e) => setLocalNotes(e.target.value)}
              placeholder="Add notes, where you found this, how you plan to use it..."
              style={textareaStyles}
              title="Write notes and thoughts about this reference"
            />
          </div>

          {/* Reference Tags */}
          <div style={fieldStyles}>
            <label style={labelStyles} title="Comma-separated tags for organization">
              Tags
            </label>
            <input
              type="text"
              value={localTags}
              onChange={(e) => setLocalTags(e.target.value)}
              placeholder="character, location, mood (comma-separated)"
              style={inputStyles}
              title="Enter tags separated by commas (e.g., character, costume, victorian)"
            />
            <div style={{
              fontSize: 'var(--nb-font-size-xs)',
              color: 'var(--nb-text-muted)',
              marginTop: 'var(--nb-space-xs)',
            }}>
              Separate tags with commas. Tags appear as colored pills on the reference node.
            </div>
          </div>

          {/* Reference Color */}
          <div style={fieldStyles}>
            <label style={labelStyles} title="Choose a color for this reference">
              Color
            </label>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(5, 1fr)',
              gap: 'var(--nb-space-xs)',
              marginBottom: 'var(--nb-space-xs)',
            }}>
              {nodeColorPalette.map((color) => (
                <button
                  key={color.value}
                  onClick={() => {
                    saveHistory();
                    updateReferenceNode(selectedReference.id, { color: color.value });
                  }}
                  style={{
                    width: '100%',
                    height: 32,
                    backgroundColor: color.value,
                    border: selectedReference.data.color === color.value
                      ? '3px solid var(--nb-text-primary)'
                      : '1px solid var(--nb-border-primary)',
                    borderRadius: 'var(--nb-radius-sm)',
                    cursor: 'pointer',
                    padding: 0,
                    transition: 'transform var(--nb-transition-fast), border var(--nb-transition-fast)',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'scale(1.1)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'scale(1)';
                  }}
                  title={color.name}
                />
              ))}
            </div>
            <div style={{ display: 'flex', gap: 'var(--nb-space-sm)', alignItems: 'center' }}>
              <input
                type="color"
                value={selectedReference.data.color || '#2d2d30'}
                onChange={(e) => {
                  saveHistory();
                  updateReferenceNode(selectedReference.id, { color: e.target.value });
                }}
                style={{
                  ...inputStyles,
                  width: 60,
                  height: 32,
                  padding: 2,
                  cursor: 'pointer',
                }}
                title="Choose a custom color"
              />
              <span style={{ fontSize: 'var(--nb-font-size-xs)', color: 'var(--nb-text-muted)' }}>
                Custom
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Handle note node properties (when in references view)
  if (activeView === 'references' && selectedNote) {
    return (
      <div style={containerStyles} data-properties="true">
        <div style={formStyles}>
          {/* Note Title */}
          <div style={fieldStyles}>
            <label style={labelStyles} title="The title of this note">
              Title
            </label>
            <input
              type="text"
              value={localNoteTitle}
              onChange={(e) => setLocalNoteTitle(e.target.value)}
              placeholder="New Note"
              style={inputStyles}
              title="Enter a title for this note"
            />
          </div>

          {/* Note Content */}
          <div style={fieldStyles}>
            <label style={labelStyles} title="The content of this note (markdown supported)">
              Content
            </label>
            <textarea
              value={localNoteContent}
              onChange={(e) => setLocalNoteContent(e.target.value)}
              placeholder="Type your note here... (markdown supported)"
              style={{
                ...textareaStyles,
                minHeight: '300px', // Larger text area for notes
                fontFamily: 'monospace', // Use monospace for markdown editing
              }}
              title="Write your note content. Markdown is supported for formatting."
            />
            <div style={{
              fontSize: 'var(--nb-font-size-xs)',
              color: 'var(--nb-text-muted)',
              marginTop: 'var(--nb-space-xs)',
            }}>
              Select the note to edit, deselect to see rendered markdown preview
            </div>
          </div>

          {/* Render Markdown Toggle */}
          <div style={fieldStyles}>
            <label style={labelStyles} title="Toggle markdown rendering">
              Render Markdown
            </label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--nb-space-sm)' }}>
              <input
                type="checkbox"
                checked={selectedNote.data.renderMarkdown !== false}
                onChange={(e) => {
                  saveHistory();
                  updateNoteNode(selectedNote.id, { renderMarkdown: e.target.checked });
                }}
                style={{ width: 20, height: 20, cursor: 'pointer' }}
                title="Enable or disable markdown rendering in view mode"
              />
              <span style={{ fontSize: 'var(--nb-font-size-sm)', color: 'var(--nb-text-secondary)' }}>
                Show formatted markdown when not selected
              </span>
            </div>
          </div>

          {/* Note Color */}
          <div style={fieldStyles}>
            <label style={labelStyles} title="Choose a color for this note's border">
              Border Color
            </label>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(5, 1fr)',
              gap: 'var(--nb-space-xs)',
              marginBottom: 'var(--nb-space-xs)',
            }}>
              {nodeColorPalette.map((color) => (
                <button
                  key={color.value}
                  onClick={() => {
                    saveHistory();
                    updateNoteNode(selectedNote.id, { color: color.value });
                  }}
                  style={{
                    width: '100%',
                    height: 32,
                    backgroundColor: color.value,
                    border: selectedNote.data.color === color.value
                      ? '3px solid var(--nb-text-primary)'
                      : '1px solid var(--nb-border-primary)',
                    borderRadius: 'var(--nb-radius-sm)',
                    cursor: 'pointer',
                    padding: 0,
                    transition: 'transform var(--nb-transition-fast), border var(--nb-transition-fast)',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'scale(1.1)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'scale(1)';
                  }}
                  title={color.name}
                />
              ))}
            </div>
            <div style={{ display: 'flex', gap: 'var(--nb-space-sm)', alignItems: 'center' }}>
              <input
                type="color"
                value={selectedNote.data.color || '#2d2d30'}
                onChange={(e) => {
                  saveHistory();
                  updateNoteNode(selectedNote.id, { color: e.target.value });
                }}
                style={{
                  ...inputStyles,
                  width: 60,
                  height: 32,
                  padding: 2,
                  cursor: 'pointer',
                }}
                title="Choose a custom border color"
              />
              <span style={{ fontSize: 'var(--nb-font-size-xs)', color: 'var(--nb-text-muted)' }}>
                Custom
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Title Node Editing - Standalone properties view (when only title is selected)
  if (selectedTitle && !selectedNode && selectedTitleIds.length > 0) {
    return (
      <div style={containerStyles} data-properties="true">
        <div style={formStyles}>
          <div style={{
            borderBottom: '1px solid var(--nb-border-primary)',
            paddingBottom: 'var(--nb-space-sm)',
            marginBottom: 'var(--nb-space-sm)',
          }}>
            <label style={{
              ...labelStyles,
              fontSize: 'var(--nb-font-size-sm)',
              fontWeight: 600,
            }}>
              Title Overlay Properties
            </label>
          </div>

          <div style={fieldStyles}>
            <label style={labelStyles}>Text Content</label>
            <textarea
              value={localTitleText}
              onChange={(e) => setLocalTitleText(e.target.value)}
              onBlur={() => {
                if (localTitleText !== selectedTitle.data.text) {
                  saveHistory();
                  updateTitleNode(selectedTitle.id, { text: localTitleText });
                }
              }}
              placeholder="Enter title text..."
              rows={4}
              style={{
                ...inputStyles,
                minHeight: '80px',
                resize: 'vertical',
                fontFamily: 'inherit',
              }}
            />
          </div>

          <div style={fieldStyles}>
            <label style={labelStyles}>Font Family</label>
            <select
              value={selectedTitle.data.fontFamily || 'Arial'}
              onChange={(e) => {
                saveHistory();
                updateTitleNode(selectedTitle.id, { fontFamily: e.target.value as TextFontFamily });
              }}
              style={inputStyles}
            >
              <option value="Arial">Arial</option>
              <option value="Helvetica">Helvetica</option>
              <option value="Times New Roman">Times New Roman</option>
              <option value="Georgia">Georgia</option>
              <option value="Courier New">Courier New</option>
              <option value="Verdana">Verdana</option>
              <option value="Impact">Impact</option>
            </select>
          </div>

          <div style={fieldStyles}>
            <label style={labelStyles}>Font Size: {selectedTitle.data.fontSize || 48}px</label>
            <input
              type="range"
              min="12"
              max="120"
              value={selectedTitle.data.fontSize || 48}
              onChange={(e) => {
                saveHistory();
                updateTitleNode(selectedTitle.id, { fontSize: parseInt(e.target.value) });
              }}
              style={{ width: '100%' }}
            />
          </div>

          <div style={fieldStyles}>
            <label style={labelStyles}>Color</label>
            <div style={{ display: 'flex', gap: 'var(--nb-space-sm)', alignItems: 'center' }}>
              <input
                type="color"
                value={selectedTitle.data.color || '#FFFFFF'}
                onChange={(e) => {
                  saveHistory();
                  updateTitleNode(selectedTitle.id, { color: e.target.value });
                }}
                style={{
                  width: '40px',
                  height: '24px',
                  border: '1px solid var(--nb-border-primary)',
                  borderRadius: 'var(--nb-radius-sm)',
                  cursor: 'pointer',
                }}
              />
              <input
                type="text"
                value={selectedTitle.data.color || '#FFFFFF'}
                onChange={(e) => {
                  saveHistory();
                  updateTitleNode(selectedTitle.id, { color: e.target.value });
                }}
                style={{ ...inputStyles, flex: 1, textTransform: 'uppercase' }}
                placeholder="#FFFFFF"
              />
            </div>
          </div>

          <div style={fieldStyles}>
            <label style={labelStyles}>Text Alignment</label>
            <select
              value={selectedTitle.data.alignment || 'center'}
              onChange={(e) => {
                saveHistory();
                updateTitleNode(selectedTitle.id, { alignment: e.target.value as TextAlignment });
              }}
              style={inputStyles}
            >
              <option value="left">Left</option>
              <option value="center">Center</option>
              <option value="right">Right</option>
            </select>
          </div>

          <div style={fieldStyles}>
            <label style={labelStyles}>Duration (seconds)</label>
            <input
              type="number"
              min="0.1"
              step="0.1"
              value={selectedTitle.data.duration}
              onChange={(e) => {
                saveHistory();
                updateTitleNodeDuration(selectedTitle.id, parseFloat(e.target.value));
              }}
              style={inputStyles}
            />
          </div>

          <div style={fieldStyles}>
            <label style={labelStyles}>Start Time (seconds)</label>
            <input
              type="number"
              min="0"
              step="0.1"
              value={selectedTitle.data.startTime.toFixed(1)}
              onChange={(e) => {
                saveHistory();
                updateTitleNodePosition(selectedTitle.id, parseFloat(e.target.value));
              }}
              style={inputStyles}
            />
          </div>

          <div style={fieldStyles}>
            <label style={labelStyles}>Position X: {Math.round((selectedTitle.data.position?.x ?? 0.5) * 100)}%</label>
            <input
              type="range"
              min="0"
              max="100"
              value={Math.round((selectedTitle.data.position?.x ?? 0.5) * 100)}
              onChange={(e) => {
                saveHistory();
                updateTitleNode(selectedTitle.id, { 
                  position: { ...selectedTitle.data.position, x: parseInt(e.target.value) / 100 },
                  presetPosition: undefined 
                });
              }}
              style={{ width: '100%' }}
            />
          </div>

          <div style={fieldStyles}>
            <label style={labelStyles}>Position Y: {Math.round((selectedTitle.data.position?.y ?? 0.5) * 100)}%</label>
            <input
              type="range"
              min="0"
              max="100"
              value={Math.round((selectedTitle.data.position?.y ?? 0.5) * 100)}
              onChange={(e) => {
                saveHistory();
                updateTitleNode(selectedTitle.id, { 
                  position: { ...selectedTitle.data.position, y: parseInt(e.target.value) / 100 },
                  presetPosition: undefined 
                });
              }}
              style={{ width: '100%' }}
            />
          </div>

          <div style={{ display: 'flex', gap: 'var(--nb-space-sm)', marginTop: 'var(--nb-space-md)' }}>
            <button
              onClick={() => deleteTitleNode(selectedTitle.id)}
              style={{ ...removeButtonStyles, flex: 1 }}
              title="Delete title (Delete key)"
            >
              Delete Title
            </button>
          </div>

          <div style={{
            fontSize: 'var(--nb-font-size-xs)',
            color: 'var(--nb-text-muted)',
            marginTop: 'var(--nb-space-sm)',
            padding: 'var(--nb-space-sm)',
            backgroundColor: 'var(--nb-bg-secondary)',
            borderRadius: 'var(--nb-radius-sm)',
          }}>
            <strong>Tip:</strong> In the timeline, drag to move, drag right edge to resize. Alt+drag to duplicate.
          </div>
        </div>
      </div>
    );
  }

  // No selection message (context-aware based on active view)
  if (activeView === 'references' && !selectedReference && !selectedNote) {
    return (
      <div style={containerStyles} data-properties="true">
        <div style={emptyStyles}>Select a reference or note to edit its properties</div>
      </div>
    );
  }

  if (activeView === 'beats' && !selectedNode) {
    return (
      <div style={containerStyles} data-properties="true">
        <div style={formStyles}>
          <div style={{
            borderBottom: '1px solid var(--nb-border-primary)',
            paddingBottom: 'var(--nb-space-sm)',
            marginBottom: 'var(--nb-space-sm)',
          }}>
            <label style={{
              ...labelStyles,
              fontSize: 'var(--nb-font-size-sm)',
              fontWeight: 600,
            }}>
              Project Settings
            </label>
          </div>

          <div style={fieldStyles}>
            <label style={labelStyles} title="The name of this project (syncs with top bar)">
              Project Name
            </label>
            <input
              type="text"
              value={localProjectName}
              onChange={(e) => setLocalProjectName(e.target.value)}
              placeholder="Untitled Project"
              style={inputStyles}
              title="Enter a name for this project"
            />
          </div>

          <div style={fieldStyles}>
            <label style={labelStyles} title="Author of this project (optional, for PDF export)">
              Author (optional)
            </label>
            <input
              type="text"
              value={localProjectAuthor}
              onChange={(e) => setLocalProjectAuthor(e.target.value)}
              placeholder="Your name"
              style={inputStyles}
              title="Enter your name (will appear on PDF export if provided)"
            />
          </div>

          <div style={fieldStyles}>
            <label style={labelStyles} title="Description of this project (optional, for PDF export)">
              Description (optional)
            </label>
            <textarea
              value={localProjectDescription}
              onChange={(e) => setLocalProjectDescription(e.target.value)}
              placeholder="Brief description of your project..."
              style={{
                ...textareaStyles,
                minHeight: 120,
              }}
              title="Enter a description of your project (will appear on PDF export if provided)"
            />
            <div style={{
              fontSize: 'var(--nb-font-size-xs)',
              color: 'var(--nb-text-muted)',
              marginTop: 'var(--nb-space-xs)',
            }}>
              This description will appear on the title page when exporting to PDF.
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={containerStyles} data-properties="true">
      <div style={formStyles}>
        {/* Title */}
        <div style={fieldStyles}>
          <label style={labelStyles} title="The name of this beat node">
            Title
          </label>
          <input
            type="text"
            value={localTitle}
            onChange={handleTitleChange}
            placeholder="Beat title"
            style={inputStyles}
            title="Enter a descriptive name for this story beat"
          />
        </div>

        {/* Duration */}
        <div style={rowFieldStyles}>
          <label style={labelStyles} title="Duration in seconds.frames format">
            Duration (sec.frames)
          </label>
          <input
            type="text"
            value={secondsToFrameNotation(selectedNode.data.duration, fps)}
            onChange={handleDurationChange}
            onKeyDown={handleDurationKeyDown}
            placeholder="0.0"
            style={numberInputStyles}
            title={`Duration in seconds.frames format at ${fps} fps (e.g., 3.12 = 3 seconds + 12 frames). Use arrow keys to increment/decrement by 1 frame.`}
          />
          <div style={durationControlsStyles}>
            <button
              onClick={handleIncrementDuration}
              style={spinnerButtonStyles}
              title="Increment by 1 frame"
            >
              ▲
            </button>
            <button
              onClick={handleDecrementDuration}
              style={spinnerButtonStyles}
              title="Decrement by 1 frame"
            >
              ▼
            </button>
          </div>
        </div>

        {/* Image */}
        <div style={fieldStyles}>
          <label style={labelStyles} title="Storyboard image for this beat">
            Image
          </label>
          <div style={rowFieldStyles}>
            <button
              onClick={handleBrowseImage}
              style={buttonStyles}
              title="Select an image file for this beat"
            >
              Browse...
            </button>
            {selectedNode.data.imagePath && (
              <>
                <span style={imageInfoStyles} title={selectedNode.data.imagePath}>
                  {selectedNode.data.imagePath}
                </span>
                <button onClick={handleRemoveImage} style={removeButtonStyles}>
                  Remove
                </button>
              </>
            )}
            {!selectedNode.data.imagePath && (
              <span style={imageInfoStyles}>No image selected</span>
            )}
          </div>
        </div>

        {/* Camera Movement Overlays (NEW) - Only show if beat has an image */}
        {selectedNode.data.imagePath && (
          <div style={fieldStyles}>
            <div
              style={{
                ...overlayHeaderStyles,
                backgroundColor: isOverlaySectionExpanded ? 'var(--nb-bg-hover)' : 'transparent',
              }}
              onClick={() => setIsOverlaySectionExpanded(!isOverlaySectionExpanded)}
              title="Click to expand/collapse camera movement overlay controls"
            >
              <span style={{ fontSize: 'var(--nb-font-size-sm)' }}>
                {isOverlaySectionExpanded ? '▼' : '▶'}
              </span>
              <label style={{ ...labelStyles, cursor: 'pointer', flex: 1 }}>
                Camera Movement
              </label>
              {selectedNode.data.overlays && selectedNode.data.overlays.length > 0 && (
                <span
                  style={{
                    fontSize: 'var(--nb-font-size-xs)',
                    color: 'var(--nb-accent-primary)',
                    fontWeight: 500,
                  }}
                >
                  {selectedNode.data.overlays.length}
                </span>
              )}
            </div>

            {isOverlaySectionExpanded && (
              <div style={overlayContentStyles}>
                {/* Add Overlay Dropdown */}
                <select
                  value=""
                  onChange={(e) => {
                    if (e.target.value) {
                      handleAddOverlay(e.target.value as CameraMovementType);
                      e.target.value = ''; // Reset dropdown
                    }
                  }}
                  style={overlayDropdownStyles}
                  title="Select a camera movement type to add"
                >
                  <option value="">+ Add Overlay...</option>
                  <option value="pan-left">Pan Left</option>
                  <option value="pan-right">Pan Right</option>
                  <option value="tilt-up">Tilt Up</option>
                  <option value="tilt-down">Tilt Down</option>
                  <option value="boom-up">Boom Up</option>
                  <option value="boom-down">Boom Down</option>
                  <option value="zoom-in">Zoom In</option>
                  <option value="zoom-out">Zoom Out</option>
                  <option value="push-in">Push In</option>
                  <option value="pull-out">Pull Back</option>
                  <option value="truck-left">Truck Left</option>
                  <option value="truck-right">Truck Right</option>
                  <option value="frame">Frame</option>
                </select>

                {/* Active Overlays List */}
                {selectedNode.data.overlays && selectedNode.data.overlays.length > 0 && (
                  <div style={{ marginTop: 'var(--nb-space-sm)' }}>
                    <label
                      style={{
                        ...labelStyles,
                        display: 'block',
                        marginBottom: 'var(--nb-space-xs)',
                      }}
                    >
                      On this beat:
                    </label>
                    {selectedNode.data.overlays.map((overlay) => {
                      const OverlaySymbol = overlaySymbols[overlay.type];
                      const isExpanded = expandedOverlayId === overlay.id;

                      return (
                        <div key={overlay.id}>
                          <div style={activeOverlayItemStyles}>
                            <div style={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                              {OverlaySymbol && (
                                <div style={overlayIconStyles}>
                                  <OverlaySymbol size={20} opacity={overlay.opacity || 0.8} color={overlay.color} />
                                </div>
                              )}
                              <span>{formatMovementName(overlay.type)}</span>
                            </div>
                            <div style={{ display: 'flex', gap: 'var(--nb-space-xs)' }}>
                              <button
                                onClick={() => setExpandedOverlayId(isExpanded ? null : overlay.id)}
                                style={{
                                  ...buttonStyles,
                                  padding: 'var(--nb-space-xs)',
                                  minWidth: '24px',
                                  backgroundColor: isExpanded ? 'var(--nb-accent-primary)' : 'transparent',
                                  color: isExpanded ? 'white' : 'var(--nb-text-secondary)',
                                }}
                                title="Customize overlay"
                              >
                                ⚙
                              </button>
                              <button
                                onClick={() => handleRemoveOverlay(overlay.id)}
                                style={{
                                  ...removeButtonStyles,
                                  padding: 'var(--nb-space-xs)',
                                  minWidth: '24px',
                                }}
                                title={`Remove ${formatMovementName(overlay.type)} overlay`}
                              >
                                ✕
                              </button>
                            </div>
                          </div>

                          {/* Expanded settings panel */}
                          {isExpanded && (
                            <div
                              style={{
                                padding: 'var(--nb-space-sm)',
                                backgroundColor: 'var(--nb-bg-primary)',
                                borderRadius: 'var(--nb-radius-sm)',
                                marginTop: 'var(--nb-space-xs)',
                                marginLeft: 'var(--nb-space-md)',
                                border: '1px solid var(--nb-border-primary)',
                              }}
                            >
                              {/* Opacity */}
                              <div style={{ marginBottom: 'var(--nb-space-sm)' }}>
                                <label style={{ ...labelStyles, display: 'block', marginBottom: 'var(--nb-space-xs)' }}>
                                  Opacity: {Math.round((overlay.opacity || 0.8) * 100)}%
                                </label>
                                <input
                                  type="range"
                                  min="0"
                                  max="100"
                                  value={Math.round((overlay.opacity || 0.8) * 100)}
                                  onChange={(e) => {
                                    updateOverlay(selectedNode.id, overlay.id, {
                                      opacity: parseInt(e.target.value) / 100,
                                    });
                                  }}
                                  style={{ width: '100%' }}
                                />
                              </div>

                              {/* Size/Scale - Hide for zoom and frame overlays (they use fixed sizing) */}
                              {overlay.type !== 'zoom-in' && overlay.type !== 'zoom-out' && overlay.type !== 'frame' && (
                                <div style={{ marginBottom: 'var(--nb-space-sm)' }}>
                                  <label style={{ ...labelStyles, display: 'block', marginBottom: 'var(--nb-space-xs)' }}>
                                    Size: {Math.round((overlay.scale || 1) * 100)}%
                                  </label>
                                  <input
                                    type="range"
                                    min={(overlay.type === 'truck-left' || overlay.type === 'truck-right' || overlay.type === 'pan-left' || overlay.type === 'pan-right' || overlay.type === 'tilt-up' || overlay.type === 'tilt-down' || overlay.type === 'boom-up' || overlay.type === 'boom-down') ? "10" : "50"}
                                    max={(overlay.type === 'truck-left' || overlay.type === 'truck-right' || overlay.type === 'pan-left' || overlay.type === 'pan-right' || overlay.type === 'tilt-up' || overlay.type === 'tilt-down' || overlay.type === 'boom-up' || overlay.type === 'boom-down') ? "100" : "200"}
                                    value={Math.round((overlay.scale || 1) * 100)}
                                    onChange={(e) => {
                                      updateOverlay(selectedNode.id, overlay.id, {
                                        scale: parseInt(e.target.value) / 100,
                                      });
                                    }}
                                    style={{ width: '100%' }}
                                  />
                                </div>
                              )}

                              {/* Frame Size - Show for zoom and frame overlays */}
                              {(overlay.type === 'zoom-in' || overlay.type === 'zoom-out' || overlay.type === 'frame') && (
                                <div style={{ marginBottom: 'var(--nb-space-sm)' }}>
                                  <label style={{ ...labelStyles, display: 'block', marginBottom: 'var(--nb-space-xs)' }}>
                                    Frame Size: {Math.round((overlay.frameSize || 0.5) * 100)}%
                                  </label>
                                  <input
                                    type="range"
                                    min="20"
                                    max="80"
                                    value={Math.round((overlay.frameSize || 0.5) * 100)}
                                    onChange={(e) => {
                                      const newFrameSize = parseInt(e.target.value) / 100;
                                      const currentPos = overlay.framePosition || { x: 0.5, y: 0.5 };

                                      // Clamp frame position to keep frame within bounds with new size
                                      const minPos = newFrameSize / 2;
                                      const maxPos = 1 - newFrameSize / 2;
                                      const clampedX = Math.max(minPos, Math.min(maxPos, currentPos.x));
                                      const clampedY = Math.max(minPos, Math.min(maxPos, currentPos.y));

                                      updateOverlay(selectedNode.id, overlay.id, {
                                        frameSize: newFrameSize,
                                        framePosition: { x: clampedX, y: clampedY },
                                      });
                                    }}
                                    style={{ width: '100%' }}
                                  />
                                </div>
                              )}

                              {/* Rotation - Hide for push-in/pull-out/zoom/frame/truck/pan/tilt/boom */}
                              {overlay.type !== 'push-in' && overlay.type !== 'pull-out' && overlay.type !== 'zoom-in' && overlay.type !== 'zoom-out' && overlay.type !== 'frame' && overlay.type !== 'truck-left' && overlay.type !== 'truck-right' && overlay.type !== 'pan-left' && overlay.type !== 'pan-right' && overlay.type !== 'tilt-up' && overlay.type !== 'tilt-down' && overlay.type !== 'boom-up' && overlay.type !== 'boom-down' && (
                                <div style={{ marginBottom: 'var(--nb-space-sm)' }}>
                                  <label style={{ ...labelStyles, display: 'block', marginBottom: 'var(--nb-space-xs)' }}>
                                    Rotation: {overlay.rotation || 0}°
                                  </label>
                                  <input
                                    type="range"
                                    min="0"
                                    max="360"
                                    value={overlay.rotation || 0}
                                    onChange={(e) => {
                                      updateOverlay(selectedNode.id, overlay.id, {
                                        rotation: parseInt(e.target.value),
                                      });
                                    }}
                                    style={{ width: '100%' }}
                                  />
                                </div>
                              )}

                              {/* Color - Hide for image-based overlays */}
                              {!overlay.imagePath && (
                                <div style={{ marginBottom: 'var(--nb-space-sm)' }}>
                                  <label style={{ ...labelStyles, display: 'block', marginBottom: 'var(--nb-space-xs)' }}>
                                    Color
                                  </label>
                                  <div style={{ display: 'flex', gap: 'var(--nb-space-sm)', alignItems: 'center' }}>
                                    <input
                                      type="color"
                                      value={overlay.color || '#FFFFFF'}
                                      onChange={(e) => {
                                        updateOverlay(selectedNode.id, overlay.id, {
                                          color: e.target.value,
                                        });
                                      }}
                                      style={{
                                        width: '40px',
                                        height: '24px',
                                        border: '1px solid var(--nb-border-primary)',
                                        borderRadius: 'var(--nb-radius-sm)',
                                        cursor: 'pointer',
                                      }}
                                    />
                                    <button
                                      onClick={() => {
                                        updateOverlay(selectedNode.id, overlay.id, {
                                          color: undefined,
                                        });
                                      }}
                                      style={{
                                        ...buttonStyles,
                                        fontSize: 'var(--nb-font-size-xs)',
                                        padding: 'var(--nb-space-xs)',
                                      }}
                                    >
                                      Reset
                                    </button>
                                  </div>
                                </div>
                              )}

                              {/* Frame Position - Show for zoom and frame overlays */}
                              {(overlay.type === 'zoom-in' || overlay.type === 'zoom-out' || overlay.type === 'frame') && (
                                <>
                                  <div style={{ marginBottom: 'var(--nb-space-sm)' }}>
                                    <label style={{ ...labelStyles, display: 'block', marginBottom: 'var(--nb-space-xs)' }}>
                                      Frame X: {Math.round((overlay.framePosition?.x || 0.5) * 100)}%
                                    </label>
                                    <input
                                      type="range"
                                      min={Math.round((overlay.frameSize || 0.5) * 50)}
                                      max={Math.round((1 - (overlay.frameSize || 0.5) / 2) * 100)}
                                      value={Math.round((overlay.framePosition?.x || 0.5) * 100)}
                                      onChange={(e) => {
                                        const currentPos = overlay.framePosition || { x: 0.5, y: 0.5 };
                                        updateOverlay(selectedNode.id, overlay.id, {
                                          framePosition: {
                                            x: parseInt(e.target.value) / 100,
                                            y: currentPos.y,
                                          },
                                        });
                                      }}
                                      style={{ width: '100%' }}
                                    />
                                  </div>
                                  <div style={{ marginBottom: 'var(--nb-space-sm)' }}>
                                    <label style={{ ...labelStyles, display: 'block', marginBottom: 'var(--nb-space-xs)' }}>
                                      Frame Y: {Math.round((overlay.framePosition?.y || 0.5) * 100)}%
                                    </label>
                                    <input
                                      type="range"
                                      min={Math.round((overlay.frameSize || 0.5) * 50)}
                                      max={Math.round((1 - (overlay.frameSize || 0.5) / 2) * 100)}
                                      value={Math.round((overlay.framePosition?.y || 0.5) * 100)}
                                      onChange={(e) => {
                                        const currentPos = overlay.framePosition || { x: 0.5, y: 0.5 };
                                        updateOverlay(selectedNode.id, overlay.id, {
                                          framePosition: {
                                            x: currentPos.x,
                                            y: parseInt(e.target.value) / 100,
                                          },
                                        });
                                      }}
                                      style={{ width: '100%' }}
                                    />
                                  </div>
                                </>
                              )}

                              {/* Position presets - Hide for push-in/pull-out/zoom/frame/truck/pan/tilt/boom (they use special positioning) */}
                              {overlay.type !== 'push-in' && overlay.type !== 'pull-out' && overlay.type !== 'zoom-in' && overlay.type !== 'zoom-out' && overlay.type !== 'frame' && overlay.type !== 'truck-left' && overlay.type !== 'truck-right' && overlay.type !== 'pan-left' && overlay.type !== 'pan-right' && overlay.type !== 'tilt-up' && overlay.type !== 'tilt-down' && overlay.type !== 'boom-up' && overlay.type !== 'boom-down' && (
                                <div>
                                  <label style={{ ...labelStyles, display: 'block', marginBottom: 'var(--nb-space-xs)' }}>
                                    Position
                                  </label>
                                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 'var(--nb-space-xs)' }}>
                                    {[
                                      { label: '↖', preset: 'top-left' as const },
                                      { label: '↑', preset: 'top-center' as const },
                                      { label: '↗', preset: 'top-right' as const },
                                      { label: '←', preset: 'left-center' as const },
                                      { label: '●', preset: 'center' as const },
                                      { label: '→', preset: 'right-center' as const },
                                      { label: '↙', preset: 'bottom-left' as const },
                                      { label: '↓', preset: 'bottom-center' as const },
                                      { label: '↘', preset: 'bottom-right' as const },
                                    ].map((item) => (
                                      <button
                                        key={item.label}
                                        onClick={() => {
                                          updateOverlay(selectedNode.id, overlay.id, {
                                            presetPosition: item.preset,
                                          });
                                        }}
                                        style={{
                                          ...buttonStyles,
                                          padding: 'var(--nb-space-xs)',
                                          backgroundColor:
                                            overlay.presetPosition === item.preset
                                              ? 'var(--nb-accent-primary)'
                                              : 'var(--nb-bg-hover)',
                                          color:
                                            overlay.presetPosition === item.preset
                                              ? 'white'
                                              : 'var(--nb-text-primary)',
                                        }}
                                        title={`Position: ${item.label}`}
                                      >
                                        {item.label}
                                      </button>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Empty state */}
                {(!selectedNode.data.overlays || selectedNode.data.overlays.length === 0) && (
                  <div
                    style={{
                      textAlign: 'center',
                      padding: 'var(--nb-space-md)',
                      color: 'var(--nb-text-muted)',
                      fontSize: 'var(--nb-font-size-xs)',
                    }}
                  >
                    No camera overlays yet. Select one from the dropdown above.
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Scene Heading */}
        <div style={{ ...fieldStyles, position: 'relative' }}>
          <label
            style={labelStyles}
            title="Optional scene heading (e.g., INT. KITCHEN - DAY). Double-click or start typing for suggestions."
          >
            Scene Heading (optional)
          </label>
          <input
            ref={sceneHeadingInputRef}
            type="text"
            value={localSceneHeading}
            onChange={handleSceneHeadingChange}
            onKeyDown={handleSceneHeadingKeyDown}
            onBlur={handleSceneHeadingBlur}
            onDoubleClick={handleSceneHeadingDoubleClick}
            placeholder="INT. LOCATION - DAY"
            style={inputStyles}
            title="Enter a scene heading. Double-click for presets, or start typing for fuzzy suggestions. Use arrow keys to navigate, Enter to select."
          />

          {/* Autocomplete Dropdown */}
          {showSceneHeadingDropdown && (() => {
            // If showAllSceneHeadings is true (double-click), show all options
            // Otherwise use fuzzy matching on the current input
            const matches = showAllSceneHeadings ? getFuzzyMatches('') : getFuzzyMatches(localSceneHeading);
            if (matches.length === 0) return null;

            return (
              <div
                ref={sceneHeadingDropdownRef}
                style={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  right: 0,
                  backgroundColor: 'var(--nb-bg-secondary)',
                  border: '1px solid var(--nb-border)',
                  borderRadius: 'var(--nb-radius-sm)',
                  boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)',
                  maxHeight: '200px',
                  overflowY: 'auto',
                  zIndex: 1000,
                  marginTop: '2px',
                }}
              >
                {matches.map((heading, index) => (
                  <div
                    key={heading}
                    onMouseDown={(e) => {
                      // Use mousedown instead of click to fire before blur closes dropdown
                      e.preventDefault();
                      handleSuggestionClick(heading);
                    }}
                    style={{
                      padding: 'var(--nb-space-sm) var(--nb-space-md)',
                      cursor: 'pointer',
                      backgroundColor: index === sceneHeadingCursorIndex
                        ? 'var(--nb-primary)'
                        : 'transparent',
                      color: index === sceneHeadingCursorIndex
                        ? 'white'
                        : 'var(--nb-text-primary)',
                      fontSize: 'var(--nb-font-size-sm)',
                    }}
                    onMouseEnter={() => setSceneHeadingCursorIndex(index)}
                  >
                    {heading}
                  </div>
                ))}
              </div>
            );
          })()}
        </div>

        {/* Script */}
        <div style={fieldStyles}>
          <label
            style={labelStyles}
            title="Write dialogue, action/scene descriptions, and other screenplay content"
          >
            Script (Fountain)
          </label>
          <textarea
            ref={scriptTextareaRef}
            value={localScript}
            onChange={handleScriptChange}
            placeholder="Action/scene description goes here.

CHARACTER
Dialogue goes here..."
            style={textareaStyles}
            title="Write action descriptions, dialogue, and screenplay content using Fountain syntax"
          />
          <span
            style={{
              fontSize: 'var(--nb-font-size-xs)',
              color: 'var(--nb-text-muted)',
              cursor: 'help',
            }}
            title={`Fountain Syntax Examples:

Action/description lines are written normally.

CHARACTER NAME (all caps)
Dialogue goes here.

(parenthetical)
More dialogue.

Transitions:
FADE OUT.
CUT TO:`}
          >
            Use Fountain syntax for screenplay formatting
          </span>
        </div>

        {/* Shelf Toggle */}
        <div style={fieldStyles}>
          <button
            onClick={handleToggleShelf}
            style={buttonStyles}
            title={selectedNodeIds.length > 1
              ? `Move ${selectedNodeIds.length} nodes to shelf`
              : (selectedNode.data.onShelf ? 'Move to timeline' : 'Move to shelf (exclude from timeline)')
            }
          >
            {selectedNodeIds.length > 1
              ? `↑ Move to Shelf (${selectedNodeIds.length} nodes)`
              : (selectedNode.data.onShelf ? '↓ Move to Timeline' : '↑ Move to Shelf')
            }
          </button>
        </div>

        {/* Node Color */}
        <div style={fieldStyles}>
          <label style={labelStyles} title="Color for this node in the node view and shelf">
            Node Color {selectedNodeIds.length > 1 && `(${selectedNodeIds.length} nodes selected)`}
          </label>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(5, 1fr)',
            gap: 'var(--nb-space-xs)',
            marginBottom: 'var(--nb-space-xs)',
          }}>
            {nodeColorPalette.map((color) => (
              <button
                key={color.value}
                onClick={() => {
                  saveHistory();
                  if (selectedNodeIds.length > 1) {
                    updateSelectedNodesColor(color.value);
                  } else {
                    updateNode(selectedNode.id, { color: color.value });
                  }
                }}
                style={{
                  width: '100%',
                  height: 32,
                  backgroundColor: color.value,
                  border: selectedNode.data.color === color.value
                    ? '3px solid var(--nb-text-primary)'
                    : '1px solid var(--nb-border-primary)',
                  borderRadius: 'var(--nb-radius-sm)',
                  cursor: 'pointer',
                  padding: 0,
                  transition: 'transform var(--nb-transition-fast), border var(--nb-transition-fast)',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'scale(1.1)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'scale(1)';
                }}
                title={color.name}
              />
            ))}
          </div>
          <div style={{ display: 'flex', gap: 'var(--nb-space-sm)', alignItems: 'center' }}>
            <input
              type="color"
              value={selectedNode.data.color || '#2d2d30'}
              onChange={selectedNodeIds.length > 1
                ? (e) => updateSelectedNodesColor(e.target.value)
                : handleColorChange
              }
              style={{
                ...inputStyles,
                width: 60,
                height: 32,
                padding: 2,
                cursor: 'pointer',
              }}
              title={selectedNodeIds.length > 1
                ? `Choose a custom color for ${selectedNodeIds.length} selected nodes`
                : "Choose a custom color"
              }
            />
            <span style={{ fontSize: 'var(--nb-font-size-xs)', color: 'var(--nb-text-muted)' }}>
              Custom
            </span>
            {selectedNode.data.color && selectedNodeIds.length === 1 && (
              <button
                onClick={handleResetColor}
                style={{...buttonStyles, marginLeft: 'auto'}}
                title="Reset to theme default color"
              >
                Reset
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default memo(PropertiesPanel);
